import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_TIMEOUT } from "../constants/api";
import { getAuth0Token } from "../lib/auth0-token";
import { useUIStore } from "../stores/ui-store";

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getAuth0Token();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const locationName = useUIStore.getState().currentLocation;
  const isAccountApplications =
    config.url != null && /\/accounts\/[^/]+\/applications\/?$/.test(config.url);
  if (locationName && config.headers && !isAccountApplications) {
    config.headers["x-location-name"] = locationName;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      const auth0Keys = Object.keys(localStorage).filter((key) => key.startsWith("@@auth0spajs@@"));
      auth0Keys.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem("auth_token");
      window.location.replace("/login");
      return Promise.reject(error);
    }

    if (status === 403) {
      if (import.meta.env.DEV) {
        console.error("[API] Forbidden:", error.response?.data);
      }
      return Promise.reject(error);
    }

    if (status === 404) {
      if (import.meta.env.DEV) {
        console.error("[API] Not Found:", error.config?.url);
      }
      return Promise.reject(error);
    }

    if (status === 400 || status === 500) {
      if (import.meta.env.DEV) {
        console.error(`[API] ${status} Error:`, error.response?.data);
      }
      return Promise.reject(error);
    }

    if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") {
      if (import.meta.env.DEV) {
        console.error("[API] Network Error:", error.message);
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
